import { Autocomplete, TextField } from '@mui/material';
import { fetchCreditLineEvaluations } from 'lib/api/decisionEngine';
import { EvaluationRow } from 'lib/api/decisionEngine/types';
import CustomAlert from 'lib/designSystem/Alert';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from 'lib/designSystem/HeaderWithActions';
import EnhancedTable from 'lib/designSystem/Table';
import { validatePermissions } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import EvaluationDetails from '../common/Details';
import NewEvaluation from '../common/NewEvaluation';

const headers = [
  {
    key: 'businessName',
    label: 'Cliente',
  },
  {
    key: 'businessIdentifier',
    label: 'RUT',
  },
  { key: 'createdAt', label: 'Fecha', type: 'date-large' },
  {
    key: 'engineName',
    label: 'Nombre motor',
  },
  { key: 'status', label: 'Resultado' },
];

const ExecutedCreditLineEvaluations: React.FC = () => {
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [evaluations, setEvaluations] = useState<EvaluationRow[]>([]);
  const [filteredEvaluations, setFilteredEvaluations] = useState<
    EvaluationRow[]
  >([]);
  const [openSuccess, setOpenSuccess] = useState(false);
  const [openFailure, setOpenFailure] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [failureMessage, setFailureMessage] = useState('');
  const [openDetails, setOpenDetails] = useState(false);
  const [selectedEvaluationId, setSelectedEvaluationId] = useState<
    number | undefined
  >();

  const setSuccessOpen = (message: string) => {
    setSuccessMessage(message);
    setOpenSuccess(true);
  };

  const setFailureOpen = (message: string) => {
    setFailureMessage(message);
    setOpenFailure(true);
  };

  const handleCreateNewBusiness = async () => {
    setOpenModal(true);
  };

  const buttons: CustomButtonProps[] = [
    {
      name: 'Nueva evaluación',
      action: () => handleCreateNewBusiness(),
      color: 'primary',
      disabled: !validatePermissions(user, 'creditHubPro_evaluation_create'),
    },
  ];

  const setUp = async () => {
    try {
      setLoading(true);
      const evaluationsResponse = await fetchCreditLineEvaluations(dispatch);
      setEvaluations(evaluationsResponse);

      setFilteredEvaluations(evaluationsResponse);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  useEffect(() => {
    void setUp();
  }, []);

  const handleDetails = async (id: number) => {
    try {
      setLoading(true);
      setSelectedEvaluationId(evaluations[id].evaluationId);
      setOpenDetails(true);
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const handleChange = (event: any, newValue: string | null) => {
    if (!newValue) return setFilteredEvaluations(evaluations);
    const filtered = evaluations.filter(
      (x: EvaluationRow) => x.businessName === newValue,
    );
    setFilteredEvaluations(filtered);
  };

  const autoCompleteFilter = (
    <Autocomplete
      options={[
        ...new Set(evaluations.map((x: EvaluationRow) => x.businessName)),
      ]}
      getOptionLabel={(option: any) => `${option}`}
      renderInput={(params) => (
        <TextField style={{ minWidth: 250 }} {...params} label="Filtrar" />
      )}
      onChange={handleChange}
      fullWidth
    />
  );

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div>
      <HeaderWithActions
        type="subHeader"
        title="Todas las evaluaciones automáticas a clientes que has realizado. Tanto por línea de crédito como singulares."
        variant="body1"
        buttons={buttons}
        item={autoCompleteFilter}
      />

      <EnhancedTable
        dataHeaders={headers}
        data={filteredEvaluations}
        showActions
        handleClickRow={handleDetails}
        rowsPerPageDefault={25}
        exportToFile="xlsx"
        lastElement
      />
      {openModal && (
        <NewEvaluation
          type="creditLine"
          open={openModal}
          setOpen={setOpenModal}
          setUp={setUp}
          setSuccessOpen={setSuccessOpen}
          setFailureOpen={setFailureOpen}
        />
      )}
      {openDetails &&
        validatePermissions(user, 'creditHubPro_evaluation_view_details') && (
          <EvaluationDetails
            evaluationId={selectedEvaluationId!}
            open={openDetails}
            setOpen={setOpenDetails}
          />
        )}
      <CustomAlert
        open={openSuccess}
        setOpen={setOpenSuccess}
        severity="success"
        title="Éxito"
        text={successMessage}
        handleClose={() => setOpenSuccess(false)}
      />
      <CustomAlert
        open={openFailure}
        setOpen={setOpenFailure}
        severity="error"
        title="Error"
        text={failureMessage}
        handleClose={() => setOpenFailure(false)}
      />
    </div>
  );
};

export default ExecutedCreditLineEvaluations;
